import { useState } from 'react';
import {
  Alert,
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { WebApiResult } from '../../networkRequest/api';
import { surepassTheme } from '../../theme/surepass';
import type { Location } from '../../networkRequest/locationsService';

type DeleteLocationDialogProps = {
  open: boolean;
  location: Location;
  /** How many checkpoints go with it. None of them are partner checkpoints, or it couldn't be deleted. */
  checkpointCount: number;
  /** Deletes the location and its checkpoints through the API. The dialog closes when it succeeds. */
  onDelete: () => Promise<WebApiResult<never>>;
  onClose: () => void;
};

/** What happens to the location's checkpoints, so nobody deletes them without knowing. */
const getCheckpointsClause = (checkpointCount: number) => {
  let clause = '';
  if (checkpointCount === 1) {
    clause = ', along with its checkpoint';
  } else if (checkpointCount > 1) {
    clause = `, along with all ${checkpointCount} of its checkpoints`;
  }
  return clause;
};

/**
 * Confirms deleting a location and any checkpoints it has. Dialog page: `xs` for a
 * confirmation, and the destructive button is `color="error"` with a label that
 * names the consequence. If the API refuses, its message shows above the buttons.
 */
const DeleteLocationDialog = ({
  open,
  location,
  checkpointCount,
  onDelete,
  onClose,
}: DeleteLocationDialogProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Below sm the dialog fills the screen (Dialog page). `fullScreen` is a prop, so it needs the hook.
  const isMobile = useMediaQuery(surepassTheme.breakpoints.down('sm'));
  const titleId = `delete-location-${location.id}-title`;
  const descriptionId = `delete-location-${location.id}-description`;

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError('');
    const result = await onDelete();
    setIsDeleting(false);
    if (result.error) {
      setDeleteError(result.error);
    } else {
      onClose();
    }
  };

  // Nothing closes the dialog while the delete is in flight, so its result isn't lost.
  // The error is cleared, so it isn't there the next time the dialog opens.
  const close = () => {
    if (!isDeleting) {
      setDeleteError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <CardHeader
        // Dialog page, header with an icon: the red warning is decoration (the title
        // already names the dialog), so it takes no label.
        avatar={<WarningAmberIcon color="error" />}
        title={`Delete ${location.name}?`}
        slotProps={{
          // The avatar drops the title to body2, so it's set back to h3 like every dialog title.
          title: { id: titleId, component: 'h2', variant: 'h3' },
          avatar: { sx: { mr: 1.5, display: 'flex' } },
          // Cancels MUI's negative margin on the action slot, so the header is 72px.
          action: { sx: { alignSelf: 'center', m: 0 } },
        }}
        action={
          // Tooltip page: a control that can be disabled is wrapped in a span, or the
          // tooltip can't open on it and MUI warns.
          <Tooltip title="Close" describeChild>
            <span>
              <IconButton aria-label="Close" onClick={close} disabled={isDeleting}>
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <DialogContent sx={{ p: 2 }}>
        {/* Dialog page: the destructive action names what is lost, and what isn't. */}
        <Typography id={descriptionId}>
          {location.name} will be permanently deleted{getCheckpointsClause(checkpointCount)}.
          Activity records related to this location will remain.
        </Typography>
      </DialogContent>

      {deleteError && (
        <Alert severity="error" sx={{ mx: 2 }}>
          {deleteError}
        </Alert>
      )}

      {/* Secondary then primary, right-aligned; equal width on mobile (Dialog page). */}
      <DialogActions sx={{ p: 2, gap: 2 }}>
        <Button
          color="inherit"
          onClick={close}
          disabled={isDeleting}
          sx={{ flex: { xs: 1, sm: 'initial' } }}
        >
          Cancel
        </Button>
        {/* Button page: an in-flight action shows `loading`. */}
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          loading={isDeleting}
          sx={{ flex: { xs: 1, sm: 'initial' } }}
        >
          Delete Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteLocationDialog;
